import React, { useMemo, useState } from 'react';
import { ReportMetadata } from '../../types';
import { parseDateLocal } from '../../services/dateService';
import { useGesperrteKonten } from '../../hooks/useGesperrteKonten';
import { gesperrtZusatz } from '../../services/gesperrteKonten';
import { getSubstitutions } from '../../services/substitutionService';
import { substitutionFigures, type SubstitutionFigures } from '../../services/substitutionProgress';
import { Calendar, User, Search, Building2, Briefcase, Clock, CheckCircle2, XCircle, ArrowUpDown, ArrowUp, ArrowDown, FileDown, UserCheck } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ListProject } from '../../services/listProject';

interface Props {
    projects: ListProject[];
    users: any[];
    onOpenProject?: (project: ListProject) => void;
}

type SortKey = 'startDate' | 'projectName' | 'coordinator' | 'substitute' | 'progress';
type SortDirection = 'asc' | 'desc';

export const AdminSubstitutionView: React.FC<Props> = ({ projects, users, onOpenProject }) => {
    const [searchTerm, setSearchTerm] = useState('');
    // Filter über Vergangenes: Gesperrte (Nr. 148) bleiben wählbar, markiert.
    const gesperrteKonten = useGesperrteKonten();
    const [userFilter, setUserFilter] = useState<string>('all');
    const [viewMode, setViewMode] = useState<'active' | 'history'>('active');
    const [sortConfig, setSortConfig] = useState<{ key: SortKey; direction: SortDirection }>({ key: 'startDate', direction: 'asc' });

    const handleSort = (key: SortKey) => {
        setSortConfig(current => ({
            key,
            direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    const { activeSubstitutions, pastSubstitutions } = useMemo(() => {
        const active: any[] = [];
        const past: any[] = [];
        const now = new Date();
        // Normalize "now" to start of day for strict comparison if needed,
        // but for expiration we usually want to check if the end date has passed.

        // Toechter buchen ihre Begehungen ins Protokoll der Mutter.
        const byId = new Map(projects.map(p => [p.id, p]));
        const nameOf = (id: string) => {
            const u = users.find(x => x.id === id);
            return u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '';
        };

        projects.forEach(p => {
            const meta = p.data?.metadata as ReportMetadata | undefined;
            const parent = p.parent_project_id ? byId.get(p.parent_project_id) : undefined;

            // Alle Vertretungseinträge des Projekts (Array-Modell inkl. Legacy-Fallback)
            getSubstitutions(meta).forEach((config, entryIdx) => {
                // Normalize End Date to End of Day
                const endDate = parseDateLocal(config.endDate);
                if (!endDate) return;
                endDate.setHours(23, 59, 59, 999);

                // Gespeicherte ID bevorzugen, sonst über E-Mail auflösen
                const subEmail = (config.substituteEmail || '').trim().toLowerCase();
                const substituteUser = users.find(u => (u.email || '').trim().toLowerCase() === subEmail);
                const substituteId = config.substituteId || substituteUser?.id;

                // Soll aus den Turnusterminen, Ist = jede Begehung im Zeitraum,
                // gleich wer sie gemacht hat (substitutionProgress).
                const stats = substitutionFigures({ project: p, entry: config, parent, substituteId, nameOf });

                const item = {
                    id: `${p.id}_${entryIdx}`,
                    project: p,
                    projectNumber: meta?.projectNumber,
                    projectName: meta?.projectName,
                    coordinator: meta?.coordinator,
                    ...config,
                    stats,
                };

                // Search Filter
                if (searchTerm) {
                    const lower = searchTerm.toLowerCase();
                    const matches =
                        (item.projectName || '').toLowerCase().includes(lower) ||
                        (item.substituteName || '').toLowerCase().includes(lower) ||
                        (item.coordinator || '').toLowerCase().includes(lower) ||
                        (item.projectNumber || '').toLowerCase().includes(lower) ||
                        stats.visitors.some(v => v.name.toLowerCase().includes(lower));
                    if (!matches) return;
                }

                // User Filter: Koordinator, Eingeteilter oder wer tatsächlich begangen hat
                if (userFilter !== 'all') {
                    const isCoordinator = p.user_id === userFilter;
                    const isSubstitute = substituteId === userFilter;
                    const hasVisited = stats.visitors.some(v => v.id === userFilter);
                    if (!isCoordinator && !isSubstitute && !hasVisited) return;
                }

                // Categorization
                // Active if flag is true AND end date is in the future (or today)
                if (config.active && endDate >= now) {
                    active.push(item);
                } else {
                    past.push(item);
                }
            });
        });

        // Sort Helper
        const sortItems = (items: any[]) => {
            return items.sort((a, b) => {
                let valA: any = a[sortConfig.key];
                let valB: any = b[sortConfig.key];

                if (sortConfig.key === 'progress') {
                    valA = a.stats.percentage;
                    valB = b.stats.percentage;
                } else if (sortConfig.key === 'substitute') {
                    valA = (a.substituteName || a.substituteEmail || '').toLowerCase();
                    valB = (b.substituteName || b.substituteEmail || '').toLowerCase();
                } else {
                    // Default string comparison for other keys
                    if (typeof valA === 'string') valA = valA.toLowerCase();
                    if (typeof valB === 'string') valB = valB.toLowerCase();
                }
                if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
                if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
                return 0;
            });
        };

        return { 
            activeSubstitutions: sortItems(active), 
            pastSubstitutions: sortItems(past) 
        };
    }, [projects, users, searchTerm, sortConfig, userFilter]);

    const formatDate = (dateStr: string) => {
        const d = parseDateLocal(dateStr);
        if (!d) return '-';
        return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Läuft der Eintrag heute noch (Enddatum einschließlich)?
    const isStillRunning = (endStr: string) => {
        const d = parseDateLocal(endStr);
        if (!d) return false;
        d.setHours(23, 59, 59, 999);
        return d >= new Date();
    };

    const shortDate = (dateStr: string) => {
        const d = parseDateLocal(dateStr);
        return d ? d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '';
    };

    // Kein Soll und nichts begangen: statt „0 / 0" sagen, warum.
    const progressLabel = (stats: SubstitutionFigures) => {
        if (stats.expected > 0) return `${stats.done} / ${stats.expected}`;
        return stats.noSchedule ? 'ohne Turnus' : 'kein Termin';
    };

    const otherVisitorNames = (stats: SubstitutionFigures): string =>
        stats.otherVisitors.map(v => v.name || 'Unbekannt').join(', ');

    const handleExportPDF = () => {
        const doc = new jsPDF();
        const items = viewMode === 'active' ? activeSubstitutions : pastSubstitutions;
        const title = viewMode === 'active' ? 'Aktive Vertretungen' : 'Vertretungs-Historie';

        doc.setFontSize(18);
        doc.text(title, 14, 20);
        doc.setFontSize(10);
        doc.text(`Erstellt am: ${new Date().toLocaleDateString('de-DE')}`, 14, 28);

        if (userFilter !== 'all') {
            const u = users.find(u => u.id === userFilter);
            if (u) doc.text(`Filter: ${u.first_name} ${u.last_name}`, 14, 33);
        }

        const tableData = items.map(item => [
            item.active ? 'Aktiv' : 'Inaktiv',
            `${formatDate(item.startDate)} - ${formatDate(item.endDate)}`,
            `${item.projectNumber || ''} ${item.projectName || ''}`,
            item.coordinator || '-',
            (item.substituteName || item.substituteEmail || '-') +
                (item.stats.otherVisitors.length > 0 ? `\nbegangen von: ${otherVisitorNames(item.stats)}` : ''),
            item.stats.expected > 0
                ? `${progressLabel(item.stats)} (${item.stats.percentage}%)`
                : progressLabel(item.stats)
        ]);

        autoTable(doc, {
            startY: userFilter !== 'all' ? 40 : 35,
            head: [['Status', 'Zeitraum', 'Projekt', 'Stamm-Koordinator', 'Vertretung', 'Fortschritt']],
            body: tableData,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [59, 130, 246] }
        });

        doc.save(`vertretungen_${viewMode}_${new Date().toISOString().split('T')[0]}.pdf`);
    };

    const SortHeader = ({ label, sortKey }: { label: string, sortKey: SortKey }) => (
        <th 
            className="px-6 py-4 cursor-pointer hover:bg-slate-100 transition-colors group select-none"
            onClick={() => handleSort(sortKey)}
        >
            <div className="flex items-center gap-2">
                {label}
                <div className={`text-slate-400 transition-opacity ${sortConfig.key === sortKey ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`}>
                    {sortConfig.key === sortKey && sortConfig.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                    {sortConfig.key !== sortKey && <ArrowUpDown className="w-3 h-3" />}
                </div>
            </div>
        </th>
    );

    const renderList = (items: any[], _isHistory: boolean) => {
        if (items.length === 0) {
            return (
                <div className="text-center py-20 bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500 font-medium">Keine Einträge gefunden.</p>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase text-[11px] tracking-wider">
                            <tr>
                                <th className="px-6 py-4">Status</th>
                                <SortHeader label="Zeitraum" sortKey="startDate" />
                                <SortHeader label="Projekt" sortKey="projectName" />
                                <SortHeader label="Stamm-Koordinator" sortKey="coordinator" />
                                <SortHeader label="Vertretung" sortKey="substitute" />
                                <SortHeader label="Fortschritt" sortKey="progress" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {items.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                                    <td className="px-6 py-4">
                                        {item.active && isStillRunning(item.endDate) ? (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Aktiv
                                            </span>
                                        ) : item.active ? (
                                             <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-600 border border-amber-100">
                                                <Clock className="w-3.5 h-3.5" /> Abgelaufen
                                            </span>
                                        ) : (
                                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-500 border border-slate-200">
                                                <XCircle className="w-3.5 h-3.5" /> Inaktiv
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-slate-700 font-medium">
                                            <Calendar className="w-4 h-4 text-slate-400" />
                                            <span>{formatDate(item.startDate)} - {formatDate(item.endDate)}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        {onOpenProject ? (
                                            <button
                                                type="button"
                                                onClick={() => onOpenProject(item.project)}
                                                className="font-bold text-slate-800 hover:text-blue-700 hover:underline text-left"
                                                title="Projekt öffnen"
                                            >
                                                {item.projectName}
                                            </button>
                                        ) : (
                                            <div className="font-bold text-slate-800">{item.projectName}</div>
                                        )}
                                        <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                                            <Building2 className="w-3 h-3" /> {item.projectNumber}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 text-slate-600">
                                            <User className="w-4 h-4 text-slate-400" />
                                            {item.coordinator || 'Unbekannt'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2 font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 w-fit">
                                            <User className="w-4 h-4" />
                                            {item.substituteName || item.substituteEmail || 'Unbekannt'}
                                        </div>
                                        {item.stats.otherVisitors.length > 0 && (
                                            <div
                                                className="flex items-center gap-1 mt-1.5 text-xs font-medium text-amber-700"
                                                title="Im Zeitraum hat jemand anderes als die eingeteilte Vertretung begangen"
                                            >
                                                <UserCheck className="w-3.5 h-3.5" />
                                                begangen von {otherVisitorNames(item.stats)}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col gap-1 w-32">
                                            <div className="flex justify-between text-xs mb-1">
                                                <span className="font-medium text-slate-700">{progressLabel(item.stats)}</span>
                                                {item.stats.expected > 0 && <span className="text-slate-400">{item.stats.percentage}%</span>}
                                            </div>
                                            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                                                <div 
                                                    className={`h-full rounded-full transition-all duration-500 ${
                                                        item.stats.percentage >= 100 ? 'bg-emerald-500' : 
                                                        item.stats.percentage >= 50 ? 'bg-blue-500' : 'bg-amber-500'
                                                    }`}
                                                    style={{ width: `${Math.min(100, item.stats.percentage)}%` }}
                                                />
                                            </div>
                                            {item.stats.missedDates.length > 0 && (
                                                <span
                                                    className="text-[11px] text-red-600 font-medium"
                                                    title={`Verpasste Termine: ${item.stats.missedDates.map(shortDate).join(', ')}`}
                                                >
                                                    verpasst: {item.stats.missedDates.slice(0, 2).map(shortDate).join(', ')}
                                                    {item.stats.missedDates.length > 2 && ` +${item.stats.missedDates.length - 2}`}
                                                </span>
                                            )}
                                            {item.stats.openDates.length > 0 && (
                                                <span
                                                    className="text-[11px] text-slate-400"
                                                    title={`Offene Termine: ${item.stats.openDates.map(shortDate).join(', ')}`}
                                                >
                                                    nächster {shortDate(item.stats.openDates[0])}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header & Controls */}
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                
                {/* View Switcher */}
                <div className="flex bg-slate-100 p-1 rounded-xl">
                    <button 
                        onClick={() => setViewMode('active')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${viewMode === 'active' ? 'bg-white text-blue-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <Clock className="w-4 h-4" /> Aktuell
                        <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full text-[11px]">{activeSubstitutions.length}</span>
                    </button>
                    <button 
                        onClick={() => setViewMode('history')}
                        className={`px-4 py-2 rounded-lg text-sm font-bold transition-all flex items-center gap-2 ${viewMode === 'history' ? 'bg-white text-slate-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                    >
                        <Briefcase className="w-4 h-4" /> Historie
                        <span className="bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full text-[11px]">{pastSubstitutions.length}</span>
                    </button>
                </div>

                <div className="flex items-center gap-2 w-full md:w-auto">
                    {/* User Filter */}
                    <div className="relative flex-1 md:w-48 group">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <select 
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            className="w-full pl-10 pr-8 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all appearance-none cursor-pointer"
                        >
                            <option value="all">Alle Mitarbeiter</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{u.first_name} {u.last_name}{gesperrtZusatz(u.id, gesperrteKonten)}</option>
                            ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                            <ArrowDown className="w-3 h-3" />
                        </div>
                    </div>

                    {/* Search */}
                    <div className="relative flex-1 md:w-64 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input 
                            type="text" 
                            placeholder="Suchen..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all"
                        />
                    </div>

                    {/* PDF Export Button */}
                    <button 
                        onClick={handleExportPDF}
                        className="p-2 bg-white border border-slate-200 rounded-xl text-slate-500 hover:text-red-600 hover:border-red-200 hover:bg-red-50 transition-all shadow-sm"
                        title="Als PDF exportieren"
                    >
                        <FileDown className="w-5 h-5" />
                    </button>
                </div>
            </div>

            {/* Content */}
            {viewMode === 'active' ? (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-center gap-2 px-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Laufende Vertretungen</h3>
                    </div>
                    {renderList(activeSubstitutions, false)}
                </div>
            ) : (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                    <div className="flex items-center gap-2 px-2">
                        <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                        <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Vergangene / Inaktive Vertretungen</h3>
                    </div>
                    {renderList(pastSubstitutions, true)}
                </div>
            )}
        </div>
    );
};
